import { Injectable } from '@angular/core';
import { Observable, map, switchMap } from 'rxjs';
import { UtilsService } from './utils.service';
import {
  KkmAvailableOperations,
  Report,
  TransactionsResponse,
} from '../modules/kkms/kkms.types';
import { KkmsApiService } from '../modules/kkms/kkms-api.service';
import { BillingStatus, IKkm, RegStatus } from '../modules/retail-places/retail-paces.types';
import { SettingsService } from './settings.service';

export interface ReportsSearchParams {
  reportType: number;
  shiftNumber?: string;
  fromDate?: string;
  toDate?: string;
}

export interface TransactionsSearchParams {
  page: number;
  shiftNumber?: string;
  fromDate?: string;
  toDate?: string;
}

@Injectable({
  providedIn: 'root',
})

export class KkmsDetailsService {
  constructor(
    private kkmsApiService: KkmsApiService,
    private settingsService: SettingsService,
    private utilsService: UtilsService,
  ) {}

  getAvailableActions(kkmRegStatus?: RegStatus, kkmBillingStatus?: BillingStatus, tariffId?: number): KkmAvailableOperations {
    const isKkmRegActive = kkmRegStatus !== undefined
      && [RegStatus.Registered, RegStatus.ReregistrationError, RegStatus.ErrorOnDeregistration].includes(kkmRegStatus);
    const regStatusAllowUpdateTariff = kkmRegStatus !== undefined
      && [RegStatus.ContractIsSigned, RegStatus.SubmittedForRegistration, RegStatus.RegistrationError, RegStatus.Registered, RegStatus.ReregistrationError, RegStatus.ErrorOnDeregistration, RegStatus.SentForReregistration].includes(kkmRegStatus);

    return {
      availableOperations: {
        registerKkm: RegStatus.Saved === kkmRegStatus && kkmBillingStatus === undefined,
        reRegisterKkm: isKkmRegActive,
        deregisterKkm: isKkmRegActive
          && [undefined, BillingStatus.WithdrawnFromRegistration, BillingStatus.Registered, BillingStatus.Activated, BillingStatus.Deactivated, BillingStatus.Blocked].includes(kkmBillingStatus),

        selectTariff: (regStatusAllowUpdateTariff && kkmBillingStatus === undefined) || (regStatusAllowUpdateTariff && Boolean(kkmBillingStatus) && kkmBillingStatus === BillingStatus.WithdrawnFromRegistration),
        changeTariff: (regStatusAllowUpdateTariff && kkmBillingStatus !== undefined
          && [BillingStatus.Registered, BillingStatus.Activated, BillingStatus.Deactivated, BillingStatus.Blocked].includes(kkmBillingStatus))
        || (!kkmBillingStatus && Boolean(tariffId)),
        activateTariff: isKkmRegActive
          && kkmBillingStatus !== undefined
          && [BillingStatus.Registered, BillingStatus.Deactivated, BillingStatus.Blocked].includes(kkmBillingStatus),
        toggleRenewal: kkmRegStatus !== undefined
          && [RegStatus.Registered, RegStatus.ReregistrationError, RegStatus.RequestToDeregisterHasBeenSent, RegStatus.ErrorOnDeregistration, RegStatus.SentForReregistration].includes(kkmRegStatus)
          && kkmBillingStatus !== undefined
          && [BillingStatus.Registered, BillingStatus.Activated, BillingStatus.Blocked].includes(kkmBillingStatus),

        getToken: isKkmRegActive
          && [undefined, BillingStatus.Registered, BillingStatus.Activated, BillingStatus.Deactivated, BillingStatus.Blocked].includes(kkmBillingStatus),
        changeOfd: isKkmRegActive,
      },
    };
  }

  getKkm(kkmId: number): Observable<IKkm & KkmAvailableOperations> {
    return this.kkmsApiService.fetchKkm(kkmId)
      .pipe(
        map(kkm => ({
          ...kkm,
          ...this.getAvailableActions(kkm.regStatus, kkm.billingStatus, kkm.tariffId),
        })),
      );
  }

  public getTransactions(kkmId: number, searchParams: TransactionsSearchParams): Observable<TransactionsResponse> {
    return this.settingsService.getSettings().pipe(
      switchMap((settings) => {
        if (!searchParams.page) {
          searchParams.page = 1;
        }
        const params = this.utilsService.getHttpParams(searchParams);

        return settings['ui.options.use.newtaxpayer'] === 'true'
          ? this.kkmsApiService.fetchTransactions(kkmId, params)
          : this.kkmsApiService.fetchTransactionsOld(kkmId, params);
      }),
    );
  }

  public getReports(kkmId: number, searchParams: ReportsSearchParams): Observable<Report[]> {
    const params = this.utilsService.getHttpParams(searchParams);
    return this.settingsService.getSettings().pipe(
      switchMap(settings => (settings['ui.options.use.newtaxpayer'] === 'true'
        ? this.kkmsApiService.fetchReports(kkmId, params)
        : this.kkmsApiService.fetchReportsOld(kkmId, params))),
    );
  }
}
